import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import { constant } from 'src/app/constant/constant';
import { TaxOverrideReportRequest } from 'src/app/models/RequestModel/TaxOverrideReportRequest';
import { TransactionSearch } from 'src/app/models/RequestModel/TransactionSearch';
import { BaseHttpService } from 'src/app/utility/BaseHttpService';

@Injectable({
  providedIn: 'root'
})
export class ReportsService {

  private readonly CLIENT_TIMEOUT = 120000;

  constructor(private httpClient: HttpClient, private baseHttpService: BaseHttpService) { }

  private postWithTimeout<T>(url: string, payload: any): Observable<T> {
    return this.baseHttpService.post<T>(url, payload, constant.httpClientHeader.headers).pipe(
      timeout(this.CLIENT_TIMEOUT)
    );
  }

  private postBlobWithTimeout(url: string, payload: any): Observable<Blob> {
    return this.httpClient.post(url, payload, { responseType: 'blob' }).pipe(
      timeout(this.CLIENT_TIMEOUT)
    );
  }

  getSalesByCustomerReportData(input: TransactionSearch) {
    return this.postWithTimeout<any>(constant.getSalesByCustomerReportDataUrl, input).pipe(map(res => res));
  }

  getSalesByDepartmentReportData(input: TransactionSearch) {
    return this.postWithTimeout<any>(constant.getSalesByDepartmentReportDataUrl, input).pipe(map(res => res));
  }

  getAllTransactionsByNPI(input: TransactionSearch) { 
    return this.postWithTimeout<any>(constant.getAllTransactionsReport, input).pipe(map(res => res));
  }

  startGenerateAllTransactionsExcel(request: any, passwordProtect: boolean = false,
    password: string | null = null) {
      var payload = {
        transactionRequest: request,
        passwordProtect: passwordProtect,
        password: password
      }
    constant.startGenerateAllTransactionsExcel
    return this.baseHttpService.post(constant.startGenerateAllTransactionsExcel, payload);
  }

  downloadGeneratedJobs(jobId: string) {
    const payload = { JobId: jobId };
    // constant.downloadGeneratedJobs
    return this.httpClient.post(
      constant.downloadGeneratedJobs, payload
    );
  }

  getSalesByItemReportData(input: TransactionSearch) {
    return this.postWithTimeout<any>(constant.getSalesByItemReportDataUrl, input).pipe(map(res => res));
  }


  getSalesBySuggestionReportData(input: TransactionSearch) {
    return this.baseHttpService.post<any>(constant.getSalesBySuggestionReportDataUrl, input, constant.httpClientHeader.headers).pipe(
      map((response: any) => {
        return response;
      })
    )
  }

  negotiateSignalR() {
    // constant.signalRNegotiateUrl
    return this.baseHttpService.post<any>(constant.signalRNegotiateUrl, {}, constant.httpClientHeader.headers).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  getSalesByPaymentReportData(input: TransactionSearch) {
    return this.postWithTimeout<any>(constant.getSalesByPaymentReportDataUrl, input).pipe(map(res => res));
  }

  getSalesByUserReportData(input: TransactionSearch) {
    return this.postWithTimeout<any>(constant.getSalesByUserReportDataUrl, input).pipe(map(res => res));
  }

  getItemPriceHistoryReportData(item: any) {
    return this.postWithTimeout<any>(constant.getItemPriceHistoryReportUrl, item).pipe(map(res => res));
  }

  getInventoryStatusReport(invReportData: any) {
    return this.postWithTimeout<any>(constant.getInventoryStatusReportUrl, invReportData).pipe(map(res => res));
  }

  getInventoryReceivedReport(invReportData: any) {
    return this.postWithTimeout<any>(constant.getInventoryReceivedReportUrl, invReportData).pipe(map(res => res));
  }

  getPayoutTransactionsForChart(input: TransactionSearch) {
    return this.postWithTimeout<any>(constant.getPayoutTransactionsForChart, input).pipe(map(res => res));
  }

  getNplexItemsReportData(input: TransactionSearch) {
    return this.postWithTimeout<any>(constant.getNplexItemsReportUrl, input).pipe(map(res => res));
  }

  getPayoutTransactionsForReport(input: TransactionSearch) {
    return this.postWithTimeout<any>(constant.getPayoutTransactionsForReport, input).pipe(map(res => res));
  }

  getTaxOverrideReport(input: TaxOverrideReportRequest) {
    return this.postWithTimeout<any>(constant.gettaxOverrideReportUrl, input).pipe(map(res => res));
  }

  getPriceOverrideReport(input: TransactionSearch) {
    return this.postWithTimeout<any>(constant.getPriceOverrideReportUrl, input).pipe(map(res => res));
  }

  getPriceOverridePDFReport(input: TransactionSearch) {
    return this.postWithTimeout<any>(constant.getPriceOverridePDFReportUrl, input).pipe(map(res => res));
  }

  getMissingItemReport(input: any) {
    return this.postWithTimeout<any>(constant.getMissingItemReportUrl, input).pipe(map(res => res));
  }

  getTransAuditReportData(input: any) {
    return this.postWithTimeout<any>(constant.getTransactionDetails, input).pipe(map(res => res));
  }

  getSalesTaxReport(input: any) {
    return this.postWithTimeout<any>(constant.getRxSalesTaxDetails, input).pipe(map(res => res));
  }

  startGenerateRxSalesTaxExcel(request: any, passwordProtect: boolean = false,password: string | null = null
  ): Observable<any> {
    const payload = {
      salesTaxRequest: request,
      passwordProtect: passwordProtect,
      password: passwordProtect && password ? password.trim() : null
    };

    return this.baseHttpService.post<any>(
      constant.startGenerateSalesTaxExcel,
      payload
    );
  }

  startGenerateRxSalesTaxPDF(request: any, passwordProtect: boolean = false, password: string | null = null
  ): Observable<any> {
    const payload = {
      salesTaxRequest: request,
      passwordProtect: passwordProtect,
      password: passwordProtect && password ? password.trim() : null
    };

    return this.baseHttpService.post<any>(
      constant.startGenerateSalesTaxPDF,
      payload
    );
  }

  startGenerateNplexExcel(request: any, passwordProtect: boolean = false, password: string | null = null): Observable<any> {
    const payload = {
      nplexRequest: request,
      passwordProtect: passwordProtect,
      password: passwordProtect && password ? password.trim() : null
    };
    return this.baseHttpService.post<any>(
      constant.startGenerateNplexExcel,
      payload
    );
  }

  startGenerateSalesByCustomerExcel(request: any, passwordProtect: boolean = false, password: string | null = null): Observable<any> {
    const payload = {
      salesByCustomerRequest: request,
      passwordProtect: passwordProtect,
      password: passwordProtect && password ? password.trim() : null
    };
    return this.baseHttpService.post<any>(
      constant.startGenerateSalesByCustomerExcel,
      payload
    );
  }

  startGenerateSalesByPaymentExcel(request: any, passwordProtect: boolean = false, password: string | null = null): Observable<any> {
    const payload = {
      transactionRequest: request,
      passwordProtect: passwordProtect,
      password: passwordProtect && password ? password.trim() : null
    };
    return this.baseHttpService.post<any>(
      constant.startGenerateSalesByPaymentExcel,
      payload
    );
  }

  startGenerateSalesByUserExcel(request: any, passwordProtect: boolean = false, password: string | null = null): Observable<any> {
    const payload = {
      transactionRequest: request,
      passwordProtect: passwordProtect,
      password: passwordProtect && password ? password.trim() : null
    };
    return this.baseHttpService.post<any>(
      constant.startGenerateSalesByUserExcel,
      payload
    );
  }

  startGeneratePriceOverrideExcel(request: any, passwordProtect: boolean = false, password: string | null = null): Observable<any> {
    const payload = {
      transactionRequest: request,
      passwordProtect: passwordProtect,
      password: passwordProtect && password ? password.trim() : null
    };
    return this.baseHttpService.post<any>(
      constant.startGeneratePriceOverrideExcel,
      payload
    );
  }

  startGenerateSalesByDepartmentExcel(request: any, passwordProtect: boolean = false, password: string | null = null): Observable<any> {
    const payload = {
      transactionRequest: request,
      passwordProtect: passwordProtect,
      password: passwordProtect && password ? password.trim() : null
    };
    return this.baseHttpService.post<any>(
      constant.startGenerateSalesByDepartmentExcel,
      payload
    );
  }

  // PDF/Blob Methods (with optional timeout)
  getSalesByPaymentPDFReportData(input: TransactionSearch) {
    return this.postBlobWithTimeout(constant.getSalesByPaymentReportPDFUrl, input);
  }

  getInventoryStatusPDFReportData(input: any) {
    return this.postBlobWithTimeout(constant.getInventoryStatusPDFReportUrl, input);
  }

  getSaleByDeptPDFReportData(input: any) {
    return this.postBlobWithTimeout(constant.getSalesByDepartmentPDFReportUrl, input);
  }

  getSalesByCustomerPDFReportData(input: TransactionSearch) {
    return this.postBlobWithTimeout(constant.getSalesByCustomerReportPDFUrl, input);
  }

  getPayoutPDFReportData(input: TransactionSearch) {
    return this.postBlobWithTimeout(constant.getPayoutReportPDFUrl, input);
  }

  getSalesByUserPDFReportData(input: TransactionSearch) {
    return this.postBlobWithTimeout(constant.getSalesByUserReportPDFUrl, input);
  }

  getSalesByItemPDFReportData(input: TransactionSearch) {
    return this.postBlobWithTimeout(constant.getSalesByItemReportPDFUrl, input);
  }

  getInventoryReceivedPDFReportData(input: any) {
    return this.postBlobWithTimeout(constant.getInventoryReceivedReportPDFUrl, input);
  }

  getMissingItemReportPdf(input: any) {
    return this.postBlobWithTimeout(constant.getMissingItemReportPdfUrl, input);
  }

  getPaymentReportData(input: TransactionSearch) {
    // constant.getPaymentReportDataUrl
    return this.postWithTimeout<any>(constant.getPaymentReportDataUrl, input).pipe(map(res => res));
  }

  /**
   * Start PDF generation job for all transactions
   * @param request Transaction search request
   * @param passwordProtect Whether to password protect the PDF
   * @param password Password for PDF protection (if enabled)
   * @returns Observable with job ID
   */
  startGenerateAllTransactionsPDF(
    request: TransactionSearch,
    passwordProtect: boolean = false,
    password: string | null = null
  ): Observable<any> {
    // Sanitize inputs
    if (!request || typeof request !== 'object') {
      throw new Error('Invalid transaction request provided');
    }

    // Sanitize password if provided
    let sanitizedPassword: string | null = null;
    if (passwordProtect && password) {
      sanitizedPassword = password.trim();
      if (sanitizedPassword.length === 0) {
        throw new Error('Password cannot be empty when password protection is enabled');
      }
    }

    const payload = {
      transactionRequest: request,
      passwordProtect: passwordProtect,
      password: sanitizedPassword
    };


    return this.baseHttpService.post<any>(
      constant.startGenerateAllTransactionsPDF,
      payload,
      constant.httpClientHeader.headers
    ).pipe(
      map((response: any) => {
        // Validate response contains jobId
        if (!response || !response.jobId) {
          throw new Error('No job ID received from server');
        }
        return response;
      })
    );
  }

  startGenerateInventoryStatusExcel(request: any, passwordProtect: boolean = false, password: string | null = null): Observable<any> {
    const payload = {
      itemRequest: request,
      passwordProtect: passwordProtect,
      password: passwordProtect && password ? password.trim() : null
    };
    return this.baseHttpService.post<any>(
      constant.startGenerateInventoryStatusExcel,
      payload
    );
  }

  startGenerateInventoryStatusPDF(request: any, passwordProtect: boolean = false, password: string | null = null): Observable<any> {
    const payload = {
      itemRequest: request,
      passwordProtect: passwordProtect,
      password: passwordProtect && password ? password.trim() : null
    };
    return this.baseHttpService.post<any>(
      constant.startGenerateInventoryStatusPDF,
      payload,
      constant.httpClientHeader.headers
    ).pipe(
      map((response: any) => {
        if (!response || !response.jobId) {
          throw new Error('No job ID received from server');
        }
        return response;
      })
    );
  }
}
